import { useState, useRef, useCallback } from 'react'
import type { CSSProperties } from 'react'
import type { Token } from '@/types'
import type { VTTCharacterData, TokenClassification } from '@/services/characterService'
import { formatModifier } from '@/services/characterService'
import { IconButton } from '@arcanecircle/ui/grimoire/icon-button'
import { SegmentedToggle } from '@arcanecircle/ui/ui/segmented-toggle'
import { normalizeStatuses } from '@/lib/statusRegistry'
import { useVTTStore } from '@/store'
import { StatusMenu } from '@/components/StatusMenu'
import { useDragOffset } from '@/hooks/useDragOffset'
import { getTokenCombatState } from '@/utils/tokenCombatState'

export interface TokenTooltipProps {
  token: Token
  position: { x: number; y: number }
  onClose: () => void
  /** Whether the viewer is the GM. Gates the disconnect-from-mob control. */
  isGM?: boolean
  /** Whether the viewer may edit this token (GM or the token's owner). Gates
   *  owner-or-GM controls like resize, rotation, statuses, following, and the
   *  HP-bar visibility toggle. */
  canEditToken?: boolean
  /** Called when the GM disconnects this token from a shared mob (clone +
   *  repoint). Only shown when characterData.isMob is true. */
  onDisconnect?: (tokenId: string) => void
  /** Owner-or-GM: flip whether other players can see this token's HP bar
   *  (gated by canEditToken). Mirrors the same control in the bottom-left
   *  selection HUD. */
  onHpBarVisibilityChange?: (tokenId: string, visible: boolean) => void
  /** GM-only: set this token's disclosure to players — 'visible' (a normal
   *  token that obeys fog-of-war and line-of-sight) or 'gm_only' (never shown
   *  to players). Server gates the update to the GM. */
  onVisibilityChange?: (
    tokenId: string,
    visibility: 'visible' | 'gm_only',
  ) => void
  /** Owner-or-GM: resize this token. width/height are in grid squares (e.g.
   *  2×2 for a Large creature). Server validates them as positive integers. */
  onSizeChange?: (tokenId: string, width: number, height: number) => void
  /** Owner-or-GM: rotate this token to a facing in degrees clockwise (0-359). */
  onRotationChange?: (tokenId: string, rotation: number) => void
  /** Owner-or-GM: set this token's status/condition keys (full replacement array). */
  onStatusesChange?: (tokenId: string, statuses: string[]) => void
  /** Owner-or-GM: set the leader token this token follows (or null to clear). */
  onFollowChange?: (tokenId: string, followsTokenId: string | null) => void
  /** GM-only: re-classify this token's linked character (player / friendly /
   *  enemy — VTT #163). Writes the character on the platform and broadcasts so
   *  the right-sidebar bank re-buckets and the enemy outline redraws. Only
   *  shown to the GM for a token that has a linked character. */
  onClassificationChange?: (
    characterId: string,
    classification: TokenClassification,
  ) => void
  /** Other tokens on the active map, for the follow-leader picker. */
  otherTokens?: Array<{ id: string; name: string }>
  characterData?: VTTCharacterData | null
  /** Owner-or-GM (lightRadius) or just owner-or-GM (torchActive): update this
   *  token's light settings. GM can set radius; player can toggle torch on
   *  their own tokens. Called with a partial updates object. */
  onLightChange?: (
    tokenId: string,
    updates: { lightRadius?: number | null; torchActive?: boolean },
  ) => void
  /** GM-only: set this token's vision-type flags (low-light vision /
   *  darkvision). Server gates the update to the GM. Called with a partial
   *  updates object. */
  onVisionTypeChange?: (
    tokenId: string,
    updates: { lowLightVision?: boolean; darkvision?: boolean },
  ) => void
}

/**
 * v5 right-click tooltip — `.ac-hud-card` chassis (glass blur) floating
 * at screen coordinates. Shows token identity + inline-editable name
 * (owner-only), then linked-character details (HP / AC / Speed / Init
 * / ability scores) when characterData is present, then token stats.
 */
export function TokenTooltip({
  token: tokenProp,
  position,
  onClose,
  onDisconnect,
  isGM,
  canEditToken,
  onHpBarVisibilityChange,
  onVisibilityChange,
  onSizeChange,
  onRotationChange,
  onStatusesChange,
  onFollowChange,
  onClassificationChange,
  onLightChange,
  onVisionTypeChange,
  otherTokens = [],
  characterData,
}: TokenTooltipProps) {
  // Read the live token from the store so in-place edits made through this
  // menu (rotation, resize, statuses, visibility, HP-bar toggle) reflect
  // immediately. The `token` prop is only a snapshot captured when the menu
  // opened — using it directly is why incremental edits required a
  // close/reopen. Fall back to the snapshot for the frame between a delete
  // and the parent unmounting us.
  const token =
    useVTTStore((s) => s.tokens.find((t) => t.id === tokenProp.id)) ?? tokenProp
  // VTT #526: the owner's private "hide my own view of this token's HP bar"
  // preference (client-only; the canvas render gate reads the same set).
  const ownHpBarHidden = useVTTStore((s) => s.ownHpBarHidden)
  const toggleOwnHpBarHidden = useVTTStore((s) => s.toggleOwnHpBarHidden)
  // GM stats-edit modal (#657) — opens the NpcTokenModal in edit mode for
  // this token's linked character.
  const openStatsEdit = useVTTStore((s) => s.openStatsEdit)
  // Compact number-input styling for the GM size editor.
  const sizeInputStyle: CSSProperties = {
    width: 36,
    fontSize: '0.6875rem',
    textAlign: 'center',
    background: 'hsl(var(--ac-dark-accent))',
    color: 'hsl(var(--ac-text-light))',
    border: 'none',
    borderRadius: 4,
    padding: '0.0625rem 0.125rem',
  }
  const clampSize = (v: number) => Math.max(1, Math.min(20, Math.round(v) || 1))
  // Token following is disabled (issue #138 — broken on the GM screen). Typed
  // `boolean`, not a literal, so the picker block below still type-checks (a
  // literal `false &&` would mark it unreachable and drop the onFollowChange
  // narrowing). One-line re-enable once the GM-side desync is fixed.
  const FOLLOW_UI_ENABLED: boolean = false
  const activeStatuses = normalizeStatuses(token.statuses)
  const toggleStatus = (key: string) => {
    if (!onStatusesChange) return
    const next = activeStatuses.includes(key)
      ? activeStatuses.filter((k) => k !== key)
      : [...activeStatuses, key]
    onStatusesChange(token.id, next)
  }
  // ── Status/condition picker state ──────────────────────────────────────────
  // The picker is a floating StatusMenu popover anchored to the right of the
  // tooltip. statusMenuOpen controls mount/unmount; statusMenuPosition holds the
  // screen coords calculated from the trigger button's bounding rect.
  const statusTriggerRef = useRef<HTMLButtonElement>(null)
  const [statusMenuOpen, setStatusMenuOpen] = useState(false)
  const [statusMenuPosition, setStatusMenuPosition] = useState<{
    x: number
    y: number
  } | null>(null)

  const closeStatusMenu = useCallback(() => {
    setStatusMenuOpen(false)
    setStatusMenuPosition(null)
  }, [])

  // Draggable + collapsible panel. Drag by the header; collapse leaves just the
  // header bar. Solves mobile: the close/collapse controls live in the header,
  // which is clamped on-screen even if the body would overflow the viewport.
  const drag = useDragOffset()
  const [collapsed, setCollapsed] = useState(false)
  const vw = typeof window !== 'undefined' ? window.innerWidth : 9999
  const vh = typeof window !== 'undefined' ? window.innerHeight : 9999
  const panelLeft = Math.max(8, Math.min(vw - 120, position.x + drag.offset.x))
  const panelTop = Math.max(8, Math.min(vh - 56, position.y + drag.offset.y))

  const handleStatusTriggerClick = () => {
    if (statusMenuOpen) {
      closeStatusMenu()
      return
    }
    const rect = statusTriggerRef.current?.getBoundingClientRect()
    if (rect) {
      setStatusMenuPosition({ x: rect.right + 8, y: rect.top })
      setStatusMenuOpen(true)
    }
  }

  // Two-step disconnect: first click arms, second confirms within 4s.
  const [disconnectArmed, setDisconnectArmed] = useState(false)
  const handleDisconnectClick = () => {
    if (!onDisconnect) return
    if (!disconnectArmed) {
      setDisconnectArmed(true)
      window.setTimeout(() => setDisconnectArmed(false), 4000)
      return
    }
    onDisconnect(token.id)
  }

  // Classification toggle (VTT #163). Three mutually-exclusive buckets, all
  // visible at once → SegmentedToggle (not a dropdown). An unset character
  // defaults the toggle to no active pill until the GM picks one.
  const classificationOptions: ReadonlyArray<{
    value: TokenClassification
    label: string
  }> = [
    { value: 'player', label: 'Player' },
    { value: 'friendly', label: 'Friendly' },
    { value: 'enemy', label: 'Enemy' },
  ]

  // Use resolver: mob tokens read instanceStats, linked tokens read the sheet.
  const combatState = getTokenCombatState(token, characterData ?? null)
  const hpMax = Math.max(1, combatState.hp.max)
  const hpCur = combatState.hp.current
  const hpTemp = combatState.hp.temp
  const hpPct = Math.max(0, Math.min(100, (hpCur / hpMax) * 100))
  const tempPct = Math.min(100 - hpPct, (hpTemp / hpMax) * 100)

  return (
    <div
      className="ac-hud-card"
      style={{
        position: 'fixed',
        left: panelLeft,
        top: panelTop,
        minWidth: 240,
        maxWidth: 280,
        padding: '0.75rem',
        zIndex: 200,
        pointerEvents: 'auto',
      }}
      onContextMenu={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      {/* Header — drag handle + color dot + name + collapse/close. */}
      <div
        onPointerDown={drag.onPointerDown}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          paddingBottom: collapsed ? 0 : '0.5rem',
          marginBottom: collapsed ? 0 : '0.5rem',
          borderBottom: collapsed
            ? 'none'
            : '1px solid hsl(var(--ac-dark-accent))',
          cursor: drag.dragging ? 'grabbing' : 'grab',
          touchAction: 'none',
        }}
      >
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: token.color,
            flexShrink: 0,
          }}
          aria-hidden
        />
        <>
            <span
              className="font-serif-ac"
              style={{
                fontSize: '0.9375rem',
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {token.name}
            </span>
            {/* Disconnect from shared mob — GM only, shown when isMob.
                Two-state: dormant uses ghost-with-warning-tint; armed
                inverts to solid warning fill via inline style override. */}
            {characterData?.isMob && isGM && onDisconnect && (
              <IconButton
                size="sm"
                variant="ghost"
                className="!w-[22px] !h-[22px]"
                style={{
                  color: disconnectArmed
                    ? 'hsl(var(--ac-text-light))'
                    : 'hsl(var(--ac-warning, var(--ac-gm-gold)))',
                  background: disconnectArmed
                    ? 'hsl(var(--ac-warning, var(--ac-gm-gold)) / 0.7)'
                    : undefined,
                }}
                title={
                  disconnectArmed
                    ? `Click again to disconnect ${token.name} from shared creature`
                    : `Disconnect ${token.name} from shared creature`
                }
                aria-label={
                  disconnectArmed
                    ? 'Confirm disconnect from shared creature'
                    : 'Disconnect from shared creature'
                }
                onClick={handleDisconnectClick}
              >
                {/* chain/unlink icon */}
                <svg
                  width={10}
                  height={10}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                  <line x1="2" y1="2" x2="22" y2="22" />
                </svg>
              </IconButton>
            )}
          </>
        {/* Edit stats — GM-only, #657. Opens the token's linked character in
            the stats-edit modal (max HP / AC / initiative). Scoped to d20
            sheets, the only systemKind that reads these fields at all — see
            the AC/Speed/Init grid below and character-format.ts. */}
        {isGM &&
          characterData &&
          token.characterId &&
          (characterData.systemKind ?? 'd20') === 'd20' && (
            <IconButton
              size="sm"
              variant="ghost"
              className="!w-[22px] !h-[22px]"
              title="Edit max HP, AC, initiative"
              aria-label="Edit token stats"
              onClick={() => openStatsEdit(token.characterId!)}
            >
              <svg width={11} height={11} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
              </svg>
            </IconButton>
          )}
        <IconButton
          size="sm"
          variant="ghost"
          className="!w-[22px] !h-[22px]"
          title={collapsed ? 'Expand' : 'Collapse'}
          aria-label={collapsed ? 'Expand' : 'Collapse'}
          aria-expanded={!collapsed}
          onClick={() => setCollapsed((c) => !c)}
        >
          <svg
            width={12}
            height={12}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              transform: collapsed ? 'rotate(180deg)' : undefined,
            }}
          >
            <polyline points="6 15 12 9 18 15" />
          </svg>
        </IconButton>
        <IconButton
          size="sm"
          variant="ghost"
          className="!w-[22px] !h-[22px]"
          title="Close"
          aria-label="Close"
          onClick={onClose}
        >
          <svg
            width={12}
            height={12}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </IconButton>
      </div>

      {!collapsed && (<>

      {/* Character details — d20 systems get the HP bar + AC/Speed/Init +
          ability grid. Non-d20 systems show a flat customStats list
          (Burning Wheel attributes, generic key/values, etc.). */}
      {characterData && (characterData.systemKind ?? 'd20') === 'd20' && (
        <>
          {/* HP bar */}
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                fontSize: '0.625rem',
                marginBottom: 4,
              }}
            >
              <span
                style={{
                  color: 'hsl(var(--ac-text-muted))',
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                }}
              >
                HP
              </span>
              <span className="font-mono-ac">
                <b style={{ color: 'hsl(var(--ac-text-light))', fontSize: '0.8125rem' }}>
                  {hpCur}
                </b>
                {hpTemp > 0 && (
                  <span style={{ color: 'hsl(var(--ac-info))' }}> +{hpTemp}</span>
                )}
                <span style={{ color: 'hsl(var(--ac-text-muted))' }}>
                  {' '}/ {hpMax}
                </span>
              </span>
            </div>
            <div className="vtt-hp-bar">
              <div className="fill" style={{ width: `${hpPct}%` }} />
              {hpTemp > 0 && (
                <div
                  className="temp"
                  style={{ left: `${hpPct}%`, width: `${tempPct}%` }}
                />
              )}
            </div>
          </div>

          {/* AC / Speed / Init */}
          <div className="vtt-stat-grid" style={{ marginTop: '0.625rem' }}>
            <div className="vtt-stat">
              <div className="k">AC</div>
              <div className="v">{characterData.stats.ac}</div>
            </div>
            <div className="vtt-stat">
              <div className="k">Speed</div>
              <div className="v">
                {characterData.stats.speed}
                <small>ft</small>
              </div>
            </div>
            <div className="vtt-stat">
              <div className="k">Init</div>
              <div className="v">{formatModifier(characterData.stats.initiative)}</div>
            </div>
          </div>

          {/* Ability scores */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(6, 1fr)',
              gap: '0.25rem',
              marginTop: '0.625rem',
            }}
          >
            {Object.entries(characterData.abilities).map(([abbrev, ability]) => (
              <div
                key={abbrev}
                className="vtt-stat"
                style={{ padding: '0.25rem', textAlign: 'center' }}
              >
                <div className="k" style={{ fontSize: '0.5rem' }}>
                  {abbrev.toUpperCase()}
                </div>
                <div className="v" style={{ fontSize: '0.875rem' }}>
                  {formatModifier(ability.mod)}
                </div>
                <div
                  style={{
                    fontSize: '0.5625rem',
                    color: 'hsl(var(--ac-text-muted))',
                    marginTop: 1,
                  }}
                >
                  {ability.score}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Non-d20 customStats — flat key/value list. Burning Wheel
          attributes, generic-system raw stats, etc. */}
      {characterData &&
        (characterData.systemKind ?? 'd20') !== 'd20' &&
        characterData.customStats &&
        characterData.customStats.length > 0 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '0.375rem',
              marginTop: '0.25rem',
            }}
          >
            {characterData.customStats.map((stat) => (
              <div
                key={stat.key}
                className="vtt-stat"
                style={{ padding: '0.375rem 0.5rem' }}
              >
                <div className="k">{stat.label}</div>
                <div className="v" style={{ fontSize: '0.875rem' }}>
                  {stat.value}
                </div>
              </div>
            ))}
          </div>
        )}

      {/* Token stats — always shown */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          columnGap: '0.625rem',
          rowGap: '0.25rem',
          marginTop: characterData ? '0.75rem' : 0,
          fontSize: '0.6875rem',
          color: 'hsl(var(--ac-text-muted))',
          paddingTop: characterData ? '0.5rem' : 0,
          borderTop: characterData
            ? '1px solid hsl(var(--ac-dark-accent))'
            : 'none',
        }}
      >
        <span>Position</span>
        <span
          className="font-mono-ac"
          style={{ color: 'hsl(var(--ac-text-light))' }}
        >
          {token.x}, {token.y}
        </span>
        <span>Size</span>
        {canEditToken && onSizeChange ? (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
              color: 'hsl(var(--ac-text-light))',
            }}
          >
            {/* Single value sets a square footprint — the canvas scales the
                token sprite by width, so width and height are kept equal
                (Large 2×2, Huge 3×3, Gargantuan 4×4). */}
            <input
              type="number"
              min={1}
              max={20}
              value={token.width}
              onChange={(e) => {
                const n = clampSize(Number(e.target.value))
                onSizeChange(token.id, n, n)
              }}
              style={sizeInputStyle}
              aria-label="Token size in grid squares"
            />
            <span style={{ color: 'hsl(var(--ac-text-muted))' }}>sq</span>
          </span>
        ) : (
          <span style={{ color: 'hsl(var(--ac-text-light))' }}>
            {token.width}×{token.height}
          </span>
        )}
        {token.visionRadius !== undefined && (
          <>
            <span>Vision</span>
            <span style={{ color: 'hsl(var(--ac-text-light))' }}>
              {token.visionRadius === 0 ? 'None' : `${token.visionRadius} sq`}
            </span>
          </>
        )}
      </div>

      {/* GM-only: control whether players see this token at all.
          'visible' = a normal token that obeys fog-of-war / line-of-sight,
          'gm_only' = never shown to players. GM always sees it. Server gates
          this update to the GM. */}
      {isGM && onVisibilityChange && (
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.5rem',
            marginTop: '0.75rem',
            paddingTop: '0.5rem',
            borderTop: '1px solid hsl(var(--ac-dark-accent))',
            fontSize: '0.6875rem',
            color: 'hsl(var(--ac-text-muted))',
            cursor: 'pointer',
          }}
          title="Control whether players can see this token. GM always sees it."
        >
          <span>Player visibility</span>
          <select
            value={token.visibility === 'gm_only' ? 'gm_only' : 'visible'}
            onChange={(e) =>
              onVisibilityChange(
                token.id,
                e.target.value as 'visible' | 'gm_only',
              )
            }
            style={{
              fontSize: '0.6875rem',
              background: 'hsl(var(--ac-dark-accent))',
              color: 'hsl(var(--ac-text-light))',
              border: 'none',
              borderRadius: 4,
              padding: '0.125rem 0.25rem',
              cursor: 'pointer',
            }}
          >
            <option value="visible">Visible</option>
            <option value="gm_only">GM only</option>
          </select>
        </label>
      )}

      {/* GM-only: re-classify this token's linked character into a sidebar bank
          bucket (VTT #163). A 3-way mutually-exclusive choice with all options
          visible → Grimoire SegmentedToggle, not a dropdown. Needs a linked
          character (classification is character data); orphan tokens have no
          row to write. Writes the platform character + broadcasts so the bank
          re-buckets and the enemy outline redraws for everyone. */}
      {isGM && onClassificationChange && token.characterId && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.375rem',
            marginTop: '0.75rem',
            paddingTop: '0.5rem',
            borderTop: '1px solid hsl(var(--ac-dark-accent))',
          }}
        >
          <span
            style={{
              fontSize: '0.6875rem',
              color: 'hsl(var(--ac-text-muted))',
            }}
          >
            Classification
          </span>
          <SegmentedToggle<TokenClassification>
            ariaLabel="Token classification"
            value={characterData?.tokenClassification ?? 'enemy'}
            onChange={(next) =>
              onClassificationChange(token.characterId as string, next)
            }
            options={classificationOptions}
          />
        </div>
      )}

      {/* GM-only: rigid-follow leader picker. When set, this token shifts with
          the chosen leader's moves. Self / cycle / cross-map links are rejected
          server-side.
          DISABLED (issue #138): following is broken on the GM screen, so the
          picker is hidden for now — the server cascade is gated off to match. */}
      {FOLLOW_UI_ENABLED && canEditToken && onFollowChange && otherTokens.length > 0 && (
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.5rem',
            marginTop: '0.75rem',
            paddingTop: '0.5rem',
            borderTop: '1px solid hsl(var(--ac-dark-accent))',
            fontSize: '0.6875rem',
            color: 'hsl(var(--ac-text-muted))',
            cursor: 'pointer',
          }}
          title="Make this token follow another token's movement."
        >
          <span>Follows</span>
          <select
            value={token.followsTokenId ?? ''}
            onChange={(e) =>
              onFollowChange(token.id, e.target.value === '' ? null : e.target.value)
            }
            style={{
              maxWidth: 140,
              fontSize: '0.6875rem',
              background: 'hsl(var(--ac-dark-accent))',
              color: 'hsl(var(--ac-text-light))',
              border: 'none',
              borderRadius: 4,
              padding: '0.0625rem 0.125rem',
              cursor: 'pointer',
            }}
          >
            <option value="">None</option>
            {otherTokens.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {/* Owner-or-GM: rotate the token. Degrees clockwise; snaps in 45° steps
          via the stepper but any 0-359 value is accepted. The canvas rotates
          the portrait around its center. */}
      {canEditToken && onRotationChange && (
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.5rem',
            marginTop: '0.75rem',
            paddingTop: '0.5rem',
            borderTop: '1px solid hsl(var(--ac-dark-accent))',
            fontSize: '0.6875rem',
            color: 'hsl(var(--ac-text-muted))',
            cursor: 'pointer',
          }}
          title="Rotate this token (degrees clockwise)."
        >
          <span>Rotation</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <input
              type="number"
              min={0}
              max={359}
              step={45}
              value={token.rotation ?? 0}
              onChange={(e) => {
                const deg = ((Math.round(Number(e.target.value) || 0) % 360) + 360) % 360
                onRotationChange(token.id, deg)
              }}
              style={{
                width: 48,
                fontSize: '0.6875rem',
                textAlign: 'center',
                background: 'hsl(var(--ac-dark-accent))',
                color: 'hsl(var(--ac-text-light))',
                border: 'none',
                borderRadius: 4,
                padding: '0.0625rem 0.125rem',
              }}
              aria-label="Token rotation in degrees"
            />
            <span style={{ color: 'hsl(var(--ac-text-muted))' }}>°</span>
          </span>
        </label>
      )}

      {/* Owner-or-GM: condition picker. Clicking the row opens a floating
          StatusMenu popover with a 4-column chip grid (multi-select, stays
          open). The inline chip row is gone — the popover replaces it. */}
      {canEditToken && onStatusesChange && (
        <>
          <div
            style={{
              marginTop: '0.75rem',
              paddingTop: '0.5rem',
              borderTop: '1px solid hsl(var(--ac-dark-accent))',
            }}
          >
            <button
              ref={statusTriggerRef}
              type="button"
              onClick={handleStatusTriggerClick}
              aria-pressed={statusMenuOpen}
              aria-label="Open condition picker"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                fontSize: '0.6875rem',
                color: 'hsl(var(--ac-text-muted))',
              }}
            >
              <span>Conditions</span>
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  color: 'hsl(var(--ac-text-light))',
                }}
              >
                {activeStatuses.length > 0 ? (
                  <span>{activeStatuses.length} active</span>
                ) : (
                  <span style={{ color: 'hsl(var(--ac-text-muted))' }}>None</span>
                )}
                <svg
                  width={10}
                  height={10}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                  style={{
                    transform: statusMenuOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform 120ms',
                    opacity: 0.5,
                  }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </button>
          </div>
          {statusMenuOpen && statusMenuPosition && (
            <StatusMenu
              position={statusMenuPosition}
              activeStatuses={activeStatuses}
              onToggle={(key) => toggleStatus(key)}
              onClearAll={() => onStatusesChange(token.id, [])}
              onClose={closeStatusMenu}
              triggerRef={statusTriggerRef}
            />
          )}
        </>
      )}

      {/* Owner-or-GM: toggle whether other players can see this token's HP
          bar. The GM and the token's owner always see it; this only affects
          other players. Mirrors the same control in the bottom-left selection
          HUD. */}
      {(isGM || canEditToken) && onHpBarVisibilityChange && (
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.5rem',
            marginTop: '0.75rem',
            paddingTop: '0.5rem',
            borderTop: '1px solid hsl(var(--ac-dark-accent))',
            fontSize: '0.6875rem',
            color: 'hsl(var(--ac-text-muted))',
            cursor: 'pointer',
          }}
          title="When off, players cannot see this token's HP bar (you always can)."
        >
          <span>Players see HP bar</span>
          <input
            type="checkbox"
            checked={token.hpBarVisibleToPlayers !== false}
            onChange={(e) => onHpBarVisibilityChange(token.id, e.target.checked)}
          />
        </label>
      )}

      {/* VTT #526: owner-only private view toggle — hide THIS token's HP bar
          from the owner's own screen. Independent of "Players see HP bar"
          (which controls what everyone else sees) and never leaves the client.
          Shown to the owning player (canEditToken && !GM); a GM sees all bars
          by role. */}
      {canEditToken && !isGM && (
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.5rem',
            marginTop: '0.5rem',
            fontSize: '0.6875rem',
            color: 'hsl(var(--ac-text-muted))',
            cursor: 'pointer',
          }}
          title="Hide this token's HP bar on your own screen only. Doesn't affect what anyone else sees."
        >
          <span>Hide HP bar in my view</span>
          <input
            type="checkbox"
            checked={ownHpBarHidden.has(token.id)}
            onChange={() => toggleOwnHpBarHidden(token.id)}
          />
        </label>
      )}

      {/* Dynamic lighting — torch toggle + light radius.
          GM: can set lightRadius and toggle torchActive on any token.
          Player: can toggle torchActive on their OWN tokens (if lightRadius is set);
                  cannot change the radius (that's GM territory). */}
      {canEditToken && onLightChange && (
        <div
          style={{
            marginTop: '0.75rem',
            paddingTop: '0.5rem',
            borderTop: '1px solid hsl(var(--ac-dark-accent))',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.375rem',
          }}
        >
          <span style={{ fontSize: '0.6875rem', color: 'hsl(var(--ac-text-muted))' }}>
            Torch / Light
          </span>
          {/* Torch toggle — owner or GM */}
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.5rem',
              fontSize: '0.6875rem',
              color: 'hsl(var(--ac-text-muted))',
              cursor: 'pointer',
            }}
          >
            <span>Torch lit</span>
            <input
              type="checkbox"
              checked={token.torchActive === true}
              disabled={!token.lightRadius}
              onChange={(e) => onLightChange(token.id, { torchActive: e.target.checked })}
            />
          </label>
          {/* Light radius — GM only */}
          {isGM && (
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '0.5rem',
                fontSize: '0.6875rem',
                color: 'hsl(var(--ac-text-muted))',
                cursor: 'pointer',
              }}
              title="Light radius in grid squares (0 = no light)"
            >
              <span>Light radius</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <input
                  type="number"
                  min={0}
                  max={30}
                  step={1}
                  value={token.lightRadius ?? 0}
                  onChange={(e) => {
                    const v = Number(e.target.value)
                    onLightChange(token.id, { lightRadius: v > 0 ? v : null })
                  }}
                  style={{
                    width: 44,
                    fontSize: '0.6875rem',
                    background: 'hsl(var(--ac-dark-accent))',
                    color: 'hsl(var(--ac-text-light))',
                    border: 'none',
                    borderRadius: 4,
                    padding: '0.125rem 0.25rem',
                    textAlign: 'right',
                  }}
                  aria-label="Light radius in grid squares"
                />
                <span style={{ color: 'hsl(var(--ac-text-muted))' }}>sq</span>
              </span>
            </label>
          )}
        </div>
      )}

      {/* Vision types — GM only. Low-light promotes dim light to bright for this
          token; darkvision lets it see its full line of sight in the dark. */}
      {isGM && onVisionTypeChange && (
        <div
          style={{
            marginTop: '0.5rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.25rem',
          }}
        >
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.5rem',
              fontSize: '0.6875rem',
              color: 'hsl(var(--ac-text-muted))',
              cursor: 'pointer',
            }}
            title="Sees the dim band as bright."
          >
            <span>Low-light vision</span>
            <input
              type="checkbox"
              checked={token.lowLightVision === true}
              onChange={(e) =>
                onVisionTypeChange(token.id, { lowLightVision: e.target.checked })
              }
            />
          </label>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.5rem',
              fontSize: '0.6875rem',
              color: 'hsl(var(--ac-text-muted))',
              cursor: 'pointer',
            }}
            title="Sees its full line of sight in darkness (rendered greyscale)."
          >
            <span>Darkvision</span>
            <input
              type="checkbox"
              checked={token.darkvision === true}
              onChange={(e) =>
                onVisionTypeChange(token.id, { darkvision: e.target.checked })
              }
            />
          </label>
        </div>
      )}

      </>)}
    </div>
  )
}
